import useToast from "@/contexts/toast-context";
import { buildErrorMessage } from "@/helpers/error-helpers";
import useLoading, { UseLoadingReturn } from "@/hooks/use-loading";
import { IdRequestProps } from "@/types/requests.types";

export type UseUpdateIdReturn<R = void> = {
  update: (props: IdRequestProps) => Promise<R>;
} & UseLoadingReturn;

export type UseUpdateIdProps<R = void> = {
  operation: (_id: number) => Promise<R>;
};

const useUpdateId = <R = void>({ operation }: UseUpdateIdProps<R>): UseUpdateIdReturn<R> => {
  const { isLoading, startLoading, endLoading, loadingIcon } = useLoading();
  const { showToast } = useToast();

  const update = async ({
    _id,
    onSuccess,
    onError,
    successMessage = "",
    errorMessage = "",
  }: IdRequestProps): Promise<R> => {
    startLoading();
    try {
      const response = await operation(_id);

      if (onSuccess) onSuccess();
      if (successMessage !== "") {
        showToast({ text: successMessage, type: "success" });
      }
      return response;
    } catch (e) {
      const message = buildErrorMessage(e, errorMessage);
      showToast({ text: message, type: "error" });
      if (onError) onError();
      throw e;
    } finally {
      endLoading();
    }
  };

  return {
    isLoading,
    startLoading,
    endLoading,
    loadingIcon,
    update,
  };
};

export default useUpdateId;
